import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  STACK_CENTER_COLUMN_DIRECTION,
  INFORMATION_STACK_WITH_BORDER,
} from '../../constants/mui-components';
import {
  testimonialsContainer,
  headerStack,
  headerTypography,
  carouselContainer,
  carouselStack,
} from './styles/testimonials.styles';
import CarouselNavigation from './components/CarouselNavigation';
import InformationStack from '../../components/stacks/InformationStack';
import CarouselCard from '../../components/cards/CarouselCard';
import testimonialData from '../../constants/home/testimonials';
import GradientDivider from '../../components/lines/GradientDivider';
import useIsMobile from '../../hooks/useIsMobile';

function Testimonials() {
  const theme = useTheme();
  const isMobile = useIsMobile('phone');
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const carouselRef = useRef(null);
  const total = testimonialData.testimonials.length;
  const testimonials = [...testimonialData.testimonials, ...testimonialData.testimonials];
  const getCardWidthPx = () => {
    if (typeof window !== 'undefined') {
      const isPhone = window.innerWidth >= theme.breakpoints.values.phone;
      const cardWidthRem = isPhone ? 25 : 15;
      const gapRem = 1;
      return (cardWidthRem + gapRem) * 16;
    }
    return (25 + 1) * 16;
  };
  const nextSlide = () => {
    if (currentIndex >= total) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const prevSlide = () => {
    if (currentIndex < 0) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const handleTransitionEnd = () => {
    if (currentIndex === total) {
      setIsTransitioning(false);
      setCurrentIndex(0);
    }
    if (currentIndex <= 0) {
      setIsTransitioning(false);
      setCurrentIndex(total);
    }
  };

  const handleDragStart = (clientX) => {
    setIsDragging(true);
    setIsTransitioning(false);
    setDragStart(clientX);
    setDragOffset(0);
  };

  const handleDragMove = (clientX) => {
    if (!isDragging) return;
    const offset = clientX - dragStart;
    setDragOffset(offset);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const cardWidthPx = getCardWidthPx();
    const threshold = cardWidthPx * 0.3;
    if (Math.abs(dragOffset) > threshold) {
      const cardsMoved = Math.round(Math.abs(dragOffset) / cardWidthPx);
      setIsTransitioning(true);
      if (dragOffset > 0) {
        setCurrentIndex((prev) => {
          const newIndex = prev - cardsMoved;
          return newIndex < 0 ? 0 : newIndex;
        });
      } else {
        setCurrentIndex((prev) => {
          const newIndex = prev + cardsMoved;
          return newIndex > total ? total : newIndex;
        });
      }
    } else {
      setIsTransitioning(true);
    }
    setDragOffset(0);
    setDragStart(0);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleDragMove(e.clientX);
  };

  const handleTouchStart = (e) => {
    handleDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    handleDragMove(e.touches[0].clientX);
  };

  const getTransform = () => {
    const baseTransformXs = `translateX(calc(-${currentIndex} * (15rem + 1rem)))`;
    const baseTransformPhone = `translateX(calc(-${currentIndex} * (25rem + 1rem)))`;
    if (isDragging && dragOffset !== 0) {
      const offsetRem = dragOffset / 16;
      return {
        xs: `${baseTransformXs} translateX(${offsetRem}rem)`,
        phone: `${baseTransformPhone} translateX(${offsetRem}rem)`,
      };
    }
    return {
      xs: baseTransformXs,
      phone: baseTransformPhone,
    };
  };

  return (
    <Stack
      className={STACK_CENTER_COLUMN_DIRECTION}
      sx={testimonialsContainer(theme)}
    >
      <Stack className={STACK_CENTER_COLUMN_DIRECTION} gap="1.5rem">
        <InformationStack
          className={INFORMATION_STACK_WITH_BORDER}
          text={testimonialData.stackText}
          variant="info_stack"
          backgroundColor={theme.palette.neutral.A600}
          textColor="white"
          fontWeight={800}
        />
        <Stack sx={headerStack}>
          <Typography variant="xlarge_2" sx={headerTypography}>
            {testimonialData.titleText}
          </Typography>
          <Typography variant="small_3" fontWeight={500} sx={headerTypography}>
            {testimonialData.subtitleText}
          </Typography>
        </Stack>
      </Stack>
      <Box
        ref={carouselRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleDragEnd}
        sx={{
          ...carouselContainer,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        <Stack
          onTransitionEnd={handleTransitionEnd}
          sx={{
            ...carouselStack(currentIndex, isTransitioning && !isDragging),
            transform: getTransform(),
            transition: (isTransitioning && !isDragging) ? 'transform 0.6s ease-out' : 'none',
          }}
        >
          {testimonials.map((testimonial) => (
            <CarouselCard
              key={testimonial.company}
              image={testimonial.image}
              company={testimonial.company}
              description={testimonial.description}
            />
          ))}
        </Stack>
        { !isMobile && (
          <CarouselNavigation onPrev={prevSlide} onNext={nextSlide} />
        )}
      </Box>
      <GradientDivider color={theme.palette.primary.A700} opacity={0.6} mt="1.5rem" />
    </Stack>
  );
}

export default Testimonials;
